import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  TicketPercent, Plus, Loader2, Trash2, Edit2, X, CheckCircle,
  ToggleLeft, ToggleRight, Tag, IndianRupee
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { code: '', description: '', min_order_amount: '', discount_pct: '', max_uses: '', is_active: true };

const AdminCoupons = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchCoupons();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCoupons = async () => {
    try {
      const r = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load coupons');
    } finally { setLoading(false); }
  };

  const openCreate = () => { setForm(EMPTY_FORM); setEditId(null); setShowForm(true); };

  const openEdit = (c) => {
    setForm({
      code: c.code,
      description: c.description || '',
      min_order_amount: String(c.min_order_amount),
      discount_pct: String(c.discount_pct),
      max_uses: c.max_uses != null ? String(c.max_uses) : '',
      is_active: c.is_active,
    });
    setEditId(c.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const pct = parseFloat(form.discount_pct);
    const minAmt = parseFloat(form.min_order_amount) || 0;
    if (!form.code.trim()) { toast.error('Coupon code is required'); return; }
    if (isNaN(pct) || pct <= 0 || pct > 100) { toast.error('Discount must be 1–100%'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        description: form.description || null,
        min_order_amount: minAmt,
        discount_pct: pct,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        is_active: form.is_active,
      };
      if (editId) {
        await axios.put(`${API}/admin/coupons/${editId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API}/admin/coupons`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Coupon created');
      }
      setShowForm(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(null); }
  };

  const handleToggle = async (c) => {
    try {
      await axios.put(`${API}/admin/coupons/${c.id}`, { is_active: !c.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(prev => prev.map(x => x.id === c.id ? { ...x, is_active: !x.is_active } : x));
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const activeCoupons = coupons.filter(c => c.is_active);

  return (
    <div className="p-6 max-w-4xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <TicketPercent className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Coupons</h1>
            <p className="text-sm text-slate-500">Create discount coupons applied by delivery boys at checkout</p>
          </div>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 rounded-xl text-sm shadow-md shadow-blue-200 transition-all hover:-translate-y-0.5">
          <Plus className="w-4 h-4" /> New Coupon
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Coupons', value: coupons.length, color: 'blue' },
          { label: 'Active', value: activeCoupons.length, color: 'emerald' },
          { label: 'Total Uses', value: coupons.reduce((s, c) => s + (c.used_count || 0), 0), color: 'orange' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`bg-white border border-slate-200/80 rounded-2xl p-4 shadow-sm border-l-4 border-l-${color}-500`}>
            <p className="text-2xl font-black text-slate-800">{value}</p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-100 rounded-2xl px-5 py-4 mb-6 flex items-start gap-4">
        <div className="text-2xl">💡</div>
        <div>
          <p className="text-sm font-bold text-slate-700 mb-1">How coupons work</p>
          <p className="text-sm text-slate-500">Delivery boys see eligible coupons during checkout based on the order total. They can apply one coupon per order. The discount is transparently shown in the order summary for both the delivery boy and the customer.</p>
        </div>
      </div>

      {/* Coupon list */}
      {coupons.length === 0 ? (
        <div className="bg-white border border-slate-200/80 rounded-[2rem] py-20 flex flex-col items-center gap-4 shadow-sm">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
            <TicketPercent className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
          </div>
          <div className="text-center">
            <p className="font-bold text-slate-500">No coupons yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first coupon to offer discounts</p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl text-sm shadow-md shadow-blue-200 transition-all">
            <Plus className="w-4 h-4" /> Create Coupon
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {coupons.map(c => (
            <div key={c.id} className={`bg-white border-2 rounded-2xl p-5 transition-all ${c.is_active ? 'border-emerald-200' : 'border-slate-100 opacity-70'}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-mono font-black text-lg text-slate-800 tracking-wider">{c.code}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {c.is_active ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  {c.description && <p className="text-xs text-slate-500">{c.description}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => handleToggle(c)}
                    className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors">
                    {c.is_active ? <ToggleRight className="w-4 h-4 text-emerald-600" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}
                  </button>
                  <button onClick={() => openEdit(c)}
                    className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center text-blue-600 transition-colors">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(c.id)} disabled={deleting === c.id}
                    className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors">
                    {deleting === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Coupon details */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 rounded-xl p-2.5 text-center">
                  <p className="text-base font-black text-emerald-700">{c.discount_pct}%</p>
                  <p className="text-[10px] text-slate-400">Discount</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <p className="text-base font-black text-slate-700">₹{c.min_order_amount}</p>
                  <p className="text-[10px] text-slate-400">Min Order</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-2.5 text-center">
                  <p className="text-base font-black text-slate-700">
                    {c.max_uses ? `${c.used_count}/${c.max_uses}` : c.used_count}
                  </p>
                  <p className="text-[10px] text-slate-400">Uses</p>
                </div>
              </div>

              {/* Example */}
              <div className="mt-3 bg-slate-50 rounded-xl px-3 py-2 text-xs text-slate-500">
                Order ≥ ₹{c.min_order_amount} → save {c.discount_pct}%
                {c.min_order_amount > 0 && ` (e.g. ₹${c.min_order_amount} order → save ₹${(c.min_order_amount * c.discount_pct / 100).toFixed(0)})`}
              </div>

              {c.created_at && (
                <p className="text-[10px] text-slate-400 mt-2">Created {format(new Date(c.created_at), 'dd MMM yyyy')}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editId ? 'Edit Coupon' : 'New Coupon'}
              </h3>
              <button onClick={() => setShowForm(false)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Coupon Code *</label>
                <input
                  type="text"
                  placeholder="e.g. SAVE10"
                  value={form.code}
                  onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  required
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors font-mono font-bold uppercase"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Description</label>
                <input
                  type="text"
                  placeholder="e.g. Get 10% off on orders above ₹500"
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Min Order (₹) *</label>
                  <input
                    type="number" min="0" step="1" placeholder="500"
                    value={form.min_order_amount}
                    onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Discount % *</label>
                  <input
                    type="number" min="1" max="100" step="0.1" placeholder="10"
                    value={form.discount_pct}
                    onChange={e => setForm(p => ({ ...p, discount_pct: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-widest mb-1.5">Max Uses (leave blank = unlimited)</label>
                <input
                  type="number" min="1" step="1" placeholder="Unlimited"
                  value={form.max_uses}
                  onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 text-sm outline-none transition-colors"
                />
              </div>

              {/* Preview */}
              {form.min_order_amount && form.discount_pct && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-sm text-emerald-700">
                  <p className="font-bold mb-0.5">Preview:</p>
                  <p>Orders ≥ ₹{form.min_order_amount} get {form.discount_pct}% off</p>
                  <p className="text-xs text-emerald-600 mt-0.5">e.g. ₹{form.min_order_amount} order → save ₹{(parseFloat(form.min_order_amount) * parseFloat(form.discount_pct) / 100).toFixed(0)}</p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${form.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                  {form.is_active ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  {form.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>

              <button type="submit" disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3 rounded-xl text-sm transition-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {editId ? 'Save Changes' : 'Create Coupon'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
